import { ChevronRight } from 'lucide-react'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ChannelPreviewUIComponentProps, DefaultStreamChatGenerics } from 'stream-chat-react'

import { paths } from 'src/config/paths'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import type { ChatType } from '../api/use-chat-data'
import { useChatData } from '../api/use-chat-data'
import { ReplyIcon } from '../icons/reply-icon'
import { ShieldIcon } from '../icons/shield-icon'
import ChatPreviewSkeleton from '../skeletons/chat-preview-skeleton'
import useChatStore from '../store/chat-store'

import ChatAvatar from './ui/chat-avatar'

const ChatPreviewContent = ({ channel }: ChannelPreviewUIComponentProps<DefaultStreamChatGenerics>) => {
  const conversationId = useChatStore((state) => state.conversationId)
  const setConversationId = useChatStore((state) => state.setConversationId)
  const { data: user } = useMyUserData()
  const { data, isLoading: isChatDataLoading } = useChatData(channel)
  const [lastMessage, setLastMessage] = useState(channel.lastMessage())
  const isChatSelected = conversationId === channel.cid

  const updateLastMessage = (event: any) => {
    setLastMessage(event.message)
  }

  useEffect(() => {
    channel.on('message.new', updateLastMessage)

    return () => {
      channel.off('message.new', updateLastMessage)
    }
  }, [channel])

  const chatType = data?.type as ChatType

  const onPressCard = useCallback(async () => {
    setConversationId(channel.cid || '')
  }, [channel.cid, setConversationId])

  const initials = useMemo(() => {
    if (!data?.title) return '??'
    const parts = data.title.trim().split(' ').filter(Boolean)
    const firstInitial = parts[0]?.charAt(0).toUpperCase() ?? ''
    const secondInitial = parts[1]?.charAt(0).toUpperCase() ?? ''
    return firstInitial || secondInitial ? `${firstInitial}${secondInitial}` : '?'
  }, [data?.title])

  const hasUnreadMessages = channel.state.unreadCount > 0

  const attachmentsLength = (lastMessage?.attachments && lastMessage?.attachments?.length) || 0

  const chatPartner = data?.chatPartner
  const isConsumerChat = data?.type === 'consumer'
  const isConsumerVerified = isConsumerChat && (chatPartner?.emailVerified || chatPartner?.phoneVerified)

  const lastMessageText =
    lastMessage?.text?.trim() === ''
      ? attachmentsLength > 0
        ? attachmentsLength === 1
          ? 'View attachment'
          : `View ${attachmentsLength} attachments`
        : 'no messages yet'
      : lastMessage?.text?.trim()

  const lastMessageSentByCurrentUser = Number(lastMessage?.user?.id || -1) === user?.id

  if (isChatDataLoading) return <ChatPreviewSkeleton />

  if (!data) return null

  return (
    <Link
      className={cn(
        'relative flex w-full cursor-pointer flex-row items-start justify-start gap-3.5 px-6 py-2.5 hover:bg-[#fafaf7]',
        (hasUnreadMessages || isChatSelected) && 'bg-[#fafaf7]',
      )}
      onClick={onPressCard}
      to={paths.chats.conversation.getHref(channel.cid || '')}
      rel="noopener noreferrer"
    >
      <div className="flex">
        <ChatAvatar
          isConsumerChat={chatType === 'consumer'}
          initials={initials}
          userId={data?.chatPartner?.id ?? null}
          size={50}
        />
      </div>
      <div className="flex flex-1 flex-col items-start justify-start gap-1">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center justify-center gap-1">
            <p className="truncate text-[15px] font-semibold">{data?.title || 'Unknown'}</p>
            {isConsumerVerified && <ShieldIcon className="size-3.5 shrink-0 text-primary" />}
          </div>
          <div className="flex max-w-[100px] flex-row items-center justify-center gap-1">
            {hasUnreadMessages && <div className="size-1.5 rounded-full bg-[#ff3131]" />}
            {lastMessage?.created_at && (
              <p className="text-right text-sm leading-none text-muted">
                {formatMessageTime(lastMessage?.created_at, { short: true })}
              </p>
            )}
            <ChevronRight className="size-4 shrink-0 text-muted" />
          </div>
        </div>
        <div className="flex w-full items-start justify-start gap-1.5">
          {!!lastMessage && !lastMessageSentByCurrentUser && <ReplyIcon className="size-4 shrink-0 text-muted" />}
          <p className={cn('line-clamp-2 text-[13px] font-medium text-black/40', lastMessage && 'text-muted')}>
            {lastMessageText || 'no messages yet'}
          </p>
        </div>
      </div>
      <div
        className={cn(
          'absolute right-0 top-0 h-full rounded-l-[15px] bg-black transition-[width] duration-500 ease-out',
          isChatSelected ? 'w-1' : 'w-0',
        )}
      />
    </Link>
  )
}

const ChatPreview = (props: ChannelPreviewUIComponentProps<DefaultStreamChatGenerics>) => {
  return (
    <Suspense fallback={<ChatPreviewSkeleton />}>
      <ChatPreviewContent {...props} />
    </Suspense>
  )
}

export default ChatPreview
